// Marketing page — "type who came from where" attribution chat plus the
// revenue it produces per channel. Three stacked sections:
//   1. Attribution chat: free-text box → worker POST /ai/attribute; the AI
//      parses ("britney came from sms this time"), files sale_attributions
//      rows, and replies. Session-only transcript in component state.
//   2. Channel cards from v_marketing_channel_revenue (week / month / all-time
//      revenue + distinct clients — all computed in the view from sales).
//   3. Attribution feed from v_sale_attributions, newest first, with a Remove
//      button (single-user tool: deleting is reversible by re-typing in chat).
// All dollar figures go through the shared privacy mask (usePrivacy/maskMoney),
// exactly like Home/Ads.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { attributeChat } from "../lib/api";
import { money } from "../lib/format";
import { maskMoney, usePrivacy } from "../lib/privacy";
import { fmtDate } from "../lib/dates";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import type { Id } from "../lib/types";

/* ---------------------------------------------------------------- channels */

/** Pretty labels for the worker's channel-slug allowlist. */
const CHANNEL_LABELS: Record<string, string> = {
  sms_marketing: "SMS marketing",
  email_marketing: "Email marketing",
  referral: "Referral",
  instagram: "Instagram",
  google_ads: "Google Ads",
  seo_gbp: "SEO (GBP)",
  event: "Event",
  other: "Other",
};

function channelLabel(slug: string | null | undefined): string {
  if (!slug) return "—";
  return CHANNEL_LABELS[slug] ?? slug;
}

/** Badge color per channel — google_ads/seo_gbp/referral match SourceBadge. */
const CHANNEL_BADGE: Record<string, string> = {
  sms_marketing: "badge badge-sky",
  email_marketing: "badge badge-amber",
  referral: "badge badge-green",
  instagram: "badge badge-mint",
  google_ads: "badge badge-accent",
  seo_gbp: "badge badge-mint",
};

function ChannelChip({ channel }: { channel: string | null | undefined }) {
  if (!channel) return null;
  return <span className={CHANNEL_BADGE[channel] ?? "badge badge-muted"}>{channelLabel(channel)}</span>;
}

/** Card gradients, cycled in order so each channel card looks distinct. */
const CARD_GRADS = ["grad-sky", "grad-mint", "grad-lavender", "grad-butter", "grad-peach", "grad-rose"];

/* ------------------------------------------------------------------- types */

/** PostgREST can serialize numerics as strings; coalesce to a number or null. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** One row of `v_marketing_channel_revenue`. */
interface ChannelRevenueRow {
  channel: string;
  sales_count: number | string | null;
  client_count: number | string | null;
  revenue_week: number | string | null;
  revenue_month: number | string | null;
  revenue_alltime: number | string | null;
}

/** One row of `v_sale_attributions` (the UI feed view). */
interface AttributionRow {
  id: Id;
  sale_id: Id;
  client_id: Id;
  channel: string;
  referred_by_client_id: Id | null;
  note: string | null;
  source: string | null;
  created_at: string;
  client_name: string | null;
  referred_by_name: string | null;
  sale_date: string | null;
  service_name: string | null;
  sale_total: number | string | null;
}

/** A filed attribution echoed into the transcript (amount already numeric). */
interface FiledLine {
  sale_id: Id;
  client_name: string;
  channel: string;
  amount: number | null;
  referred_by_name: string | null;
}

type ChatEntry =
  | { kind: "owner"; text: string }
  | { kind: "ai"; text: string; filed: FiledLine[] }
  | { kind: "error"; text: string };

/* -------------------------------------------------------------------- page */

const FEED_LIMIT = 200;

export default function Marketing() {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [feedActionError, setFeedActionError] = useState<string | null>(null);
  // Privacy mode (MetricsBar 👁 toggle): dollar figures mask to "$ ••••".
  const [privacy] = usePrivacy();
  const threadRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const dollars = (v: number | string | null | undefined) => maskMoney(money(v), privacy);
  // AI replies quote real sale amounts verbatim, so with privacy on the
  // bubble text itself must be masked too, not just the code-built numbers.
  const maskText = (text: string) =>
    privacy ? text.replace(/\$\s?\d[\d,]*(?:\.\d+)?/g, "$ ••••") : text;

  const channels = useQuery<ChannelRevenueRow[]>(async () => {
    const { data, error } = await supabase.from("v_marketing_channel_revenue").select("*");
    if (error) throw new Error(error.message);
    return (data ?? []) as ChannelRevenueRow[];
  }, []);

  const feed = useQuery<AttributionRow[]>(async () => {
    const { data, error } = await supabase
      .from("v_sale_attributions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(FEED_LIMIT);
    if (error) throw new Error(error.message);
    return (data ?? []) as AttributionRow[];
  }, []);

  // Keep the transcript pinned to the newest message.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries.length, sending]);

  async function handleSend() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setEntries((prev) => [...prev, { kind: "owner", text }]);
    setDraft("");
    try {
      const res = await attributeChat(text);
      const filed: FiledLine[] = (res.filed ?? []).map((f) => ({
        sale_id: f.sale_id,
        client_name: f.client_name,
        channel: f.channel,
        amount: num(f.amount),
        referred_by_name: f.referred_by_name ?? null,
      }));
      setEntries((prev) => [...prev, { kind: "ai", text: res.reply, filed }]);
      if (filed.length > 0) {
        // New attributions landed — the cards + feed below must reflect them.
        channels.reload();
        feed.reload();
      }
    } catch (err) {
      setEntries((prev) => [
        ...prev,
        { kind: "error", text: err instanceof Error ? err.message : String(err) },
      ]);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }

  async function removeAttribution(row: AttributionRow) {
    setRemovingId(String(row.id));
    setFeedActionError(null);
    try {
      const { error } = await supabase.from("sale_attributions").delete().eq("id", row.id);
      if (error) throw new Error(error.message);
      feed.reload();
      channels.reload();
    } catch (err) {
      setFeedActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemovingId(null);
    }
  }

  // Channel cards, biggest earner first.
  const channelRows = useMemo(() => {
    const rows = [...(channels.data ?? [])];
    rows.sort((a, b) => (num(b.revenue_alltime) ?? 0) - (num(a.revenue_alltime) ?? 0));
    return rows;
  }, [channels.data]);

  const feedColumns: Column<AttributionRow>[] = [
    {
      key: "sale_date",
      label: "Sale date",
      width: "110px",
      render: (r) => <span className="nowrap">{fmtDate(r.sale_date)}</span>,
      sortValue: (r) => r.sale_date,
    },
    {
      key: "client_name",
      label: "Client",
      render: (r) => (
        <Link to={`/clients/${String(r.client_id)}`}>
          {r.client_name || `Client ${String(r.client_id)}`}
        </Link>
      ),
      sortValue: (r) => r.client_name,
    },
    {
      key: "channel",
      label: "Channel",
      width: "150px",
      render: (r) => <ChannelChip channel={r.channel} />,
      sortValue: (r) => channelLabel(r.channel),
    },
    {
      key: "service_name",
      label: "Service",
      render: (r) => r.service_name ?? <span className="muted">—</span>,
      sortValue: (r) => r.service_name,
    },
    {
      key: "sale_total",
      label: "Amount",
      align: "right",
      width: "100px",
      render: (r) => dollars(r.sale_total),
      sortValue: (r) => num(r.sale_total),
    },
    {
      key: "referred_by_name",
      label: "Referred by",
      render: (r) =>
        r.referred_by_name ? (
          r.referred_by_client_id != null ? (
            <Link to={`/clients/${String(r.referred_by_client_id)}`}>{r.referred_by_name}</Link>
          ) : (
            r.referred_by_name
          )
        ) : (
          <span className="muted">—</span>
        ),
      sortValue: (r) => r.referred_by_name,
    },
    {
      key: "note",
      label: "Note",
      render: (r) =>
        r.note ? (
          <span className="muted truncate" style={{ display: "inline-block", maxWidth: 220 }} title={r.note}>
            {r.note}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
      sortValue: (r) => r.note,
    },
    {
      key: "created_at",
      label: "Filed",
      width: "110px",
      render: (r) => <span className="nowrap muted">{fmtDate(r.created_at)}</span>,
      sortValue: (r) => r.created_at,
    },
    {
      key: "actions",
      label: "",
      width: "90px",
      render: (r) => (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={removingId === String(r.id)}
          onClick={() => void removeAttribution(r)}
          title="Remove this attribution (re-type in the chat above to re-file it)"
        >
          {removingId === String(r.id) ? "Removing…" : "Remove"}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Marketing</h1>
          <div className="page-sub">
            Tell the chat who came from where — revenue per channel is computed from their sales.
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">Attribution chat</div>
        <div className="thread" ref={threadRef} style={{ maxHeight: "40vh", minHeight: 100 }}>
          {entries.length === 0 && (
            <div className="empty-state">
              Type who came from where — e.g. “britney came from sms this time” or “allen livitz
              this week came in from text marketing + his friend” — and the sale gets attributed
              to that channel.
            </div>
          )}
          {entries.map((e, i) => {
            if (e.kind === "owner") {
              return (
                <div key={i} className="bubble bubble-out">
                  {e.text}
                </div>
              );
            }
            if (e.kind === "error") {
              return (
                <div key={i} className="bubble bubble-in" style={{ color: "var(--red)" }}>
                  ✕ {e.text}
                </div>
              );
            }
            return (
              <div key={i} className="bubble bubble-in">
                {maskText(e.text)}
                {e.filed.map((f, j) => (
                  <div key={j} className="bubble-meta">
                    Filed: {f.client_name} → {channelLabel(f.channel)} ·{" "}
                    {maskMoney(money(f.amount), privacy)}
                    {f.referred_by_name ? ` · referred by ${f.referred_by_name}` : ""}
                  </div>
                ))}
              </div>
            );
          })}
          {sending && (
            <div className="bubble bubble-in muted">
              <span className="spinner" /> Thinking…
            </div>
          )}
        </div>
        <div className="compose">
          <textarea
            ref={textareaRef}
            value={draft}
            maxLength={1000}
            placeholder="britney came from sms this time… / allen livitz this week came from text marketing + his friend"
            disabled={sending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            aria-label="Attribution message"
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={sending || draft.trim().length === 0}
            onClick={() => void handleSend()}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Revenue by channel</div>
        {channels.error && <div className="error-banner">{channels.error}</div>}
        {channels.loading && !channels.data ? (
          <div className="loading">
            <span className="spinner" /> Loading channels…
          </div>
        ) : channelRows.length === 0 ? (
          <div className="empty-state">
            Nothing attributed yet — type who came from where in the chat above and revenue starts
            accruing here.
          </div>
        ) : (
          <div className="home-metric-grid">
            {channelRows.map((c, i) => (
              <div key={c.channel} className={`metric-card ${CARD_GRADS[i % CARD_GRADS.length]}`}>
                <div className="metric-card-label">{channelLabel(c.channel)}</div>
                <div className="metric-card-value">{dollars(c.revenue_alltime)}</div>
                <div className="metric-card-sub">
                  all time · {num(c.client_count) ?? 0}{" "}
                  {(num(c.client_count) ?? 0) === 1 ? "client" : "clients"}
                </div>
                <div className="metric-card-sub">
                  This week {dollars(c.revenue_week)} · This month {dollars(c.revenue_month)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Attribution feed</div>
        {feedActionError && <div className="error-banner">{feedActionError}</div>}
        {feed.error && <div className="error-banner">{feed.error}</div>}
        {feed.loading && !feed.data ? (
          <div className="loading">
            <span className="spinner" /> Loading attributions…
          </div>
        ) : (
          <DataTable<AttributionRow>
            columns={feedColumns}
            rows={feed.data ?? []}
            rowKey={(r) => String(r.id)}
            defaultSortKey="created_at"
            defaultSortDir="desc"
            emptyMessage="No attributions yet — they appear here as you file them in the chat."
          />
        )}
        <div className="table-footer">
          <span>
            One attribution per sale — re-attributing a sale in the chat replaces the old channel.
          </span>
          <button
            type="button"
            className="btn-link small"
            onClick={() => {
              feed.reload();
              channels.reload();
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
